// VirtualCraftList.js - Fixed to fill parent container height
import React from 'react';
import { Element, useEditor, useNode } from "@craftjs/core";
import {
    FormControl,
    FormLabel,
    TextField,
    Button,
    IconButton,
    Box,
    FormControlLabel,
    Checkbox
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { useList } from '../../../List/ListContext';
import { ItemContext } from './useTemplateString';

export function VirtualCraftList({
    name = "",
    onItemClick = null,
    clickable = true,
    displayFields = [
        { key: '_id', label: 'ID' }
    ],
    showAllFields = false,
    width = "100%",
    height = "100%", // Changed default to 100%
    ...props
}) {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    const { items, loading, error } = useList();
    const { query } = useEditor((state) => ({ query: state.query }));

    if (error) {
        return <div>Error loading items: {error.message}</div>;
    }

    const getItemTemplate = () => {
        const nodes = query.getNodes();
        const templateNode = Object.values(nodes).find(node =>
            node.id === 'item-container-template'
        );
        return templateNode;
    };

    const templateNode = getItemTemplate();

    // Selection styling - only show when editor is enabled
    const getSelectionStyle = () => {
        if (!enabled) return {};

        if (isSelected) {
            return {
                outline: '1px dashed #2196f3',
                outlineOffset: '2px'
            };
        }
        if (isHovered) {
            return {
                outline: '1px dashed #ff9800',
                outlineOffset: '1px'
            };
        }
        return {};
    };

    return (
        <div
            ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
            name={name}
            style={{
                width,
                height,
                overflow: 'auto',
                transition: 'all 0.2s ease',
                position: 'relative',
                boxSizing: 'border-box',
                ...getSelectionStyle()
            }}
            {...props}
        >
            {items.map((item, index) => {
                const contextValue = {
                    item,
                    index,
                    isListView: true
                };

                return (
                    <ItemContext.Provider key={item._id || index} value={contextValue}>
                        <div
                            onClick={() => clickable && onItemClick && onItemClick(item)}
                            style={{
                                cursor: clickable ? 'pointer' : 'default',
                                marginBottom: '8px'
                            }}
                        >
                            {templateNode ? (
                                <RenderCraftTemplate node={templateNode} />
                            ) : (
                                <div style={{ padding: '12px', border: '1px solid #eee' }}>
                                    {showAllFields ? (
                                        Object.entries(item).map(([key, value]) => (
                                            <div key={key} style={{ marginBottom: '4px' }}>
                                                <strong>{key}:</strong> {String(value)}
                                            </div>
                                        ))
                                    ) : (
                                        displayFields.map(field => (
                                            <div key={field.key} style={{ marginBottom: '4px' }}>
                                                {field.label ? (
                                                    <>
                                                        <strong>{field.label}:</strong> {String(item[field.key] || '')}
                                                    </>
                                                ) : (
                                                    String(item[field.key] || '')
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </ItemContext.Provider>
                );
            })}

            {loading && <div>Loading...</div>}
            {items.length === 0 && !loading && <div>No items found</div>}
        </div>
    );
}

const VirtualCraftListSettings = () => {
    const {
        actions: { setProp },
        name, width, height, clickable, displayFields, showAllFields
    } = useNode((node) => ({
        name: node.data.props.name,
        width: node.data.props.width,
        height: node.data.props.height,
        clickable: node.data.props.clickable,
        displayFields: node.data.props.displayFields,
        showAllFields: node.data.props.showAllFields
    }));

    const addField = () => {
        setProp(props => {
            props.displayFields = [...props.displayFields, {
                key: `field${props.displayFields.length + 1}`,
                label: `Field ${props.displayFields.length + 1}`
            }];
        });
    };

    const removeField = (index) => {
        setProp(props => {
            props.displayFields = props.displayFields.filter((_, i) => i !== index);
        });
    };

    const updateField = (index, field, value) => {
        setProp(props => {
            props.displayFields[index][field] = value;
        });
    };

    return (
        <div>
            <FormControl fullWidth margin="normal">
                <FormLabel>Component Name</FormLabel>
                <TextField
                    value={name || ''}
                    onChange={(e) => setProp(props => props.name = e.target.value)}
                    size="small"
                    placeholder="Enter component name"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Width</FormLabel>
                <TextField
                    value={width || ''}
                    onChange={(e) => setProp(props => props.width = e.target.value)}
                    size="small"
                    placeholder="100%, 300px, auto"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Height</FormLabel>
                <TextField
                    value={height || ''}
                    onChange={(e) => setProp(props => props.height = e.target.value)}
                    size="small"
                    placeholder="100%, 400px, 50vh, auto"
                />
            </FormControl>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={clickable}
                        onChange={(e) => setProp(props => props.clickable = e.target.checked)}
                    />
                }
                label="Clickable Items"
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={showAllFields}
                        onChange={(e) => setProp(props => props.showAllFields = e.target.checked)}
                    />
                }
                label="Show All Fields (ignore selected fields below)"
            />

            <FormControl fullWidth margin="normal">
                <FormLabel>Display Fields (Select which fields to show)</FormLabel>
                <Box>
                    {displayFields?.map((field, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1} marginBottom={1}>
                            <TextField
                                size="small"
                                placeholder="Field Key"
                                value={field.key}
                                onChange={(e) => updateField(index, 'key', e.target.value)}
                                style={{ flex: 1 }}
                            />
                            <TextField
                                size="small"
                                placeholder="Display Label"
                                value={field.label}
                                onChange={(e) => updateField(index, 'label', e.target.value)}
                                style={{ flex: 2 }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => removeField(index)}
                                disabled={displayFields.length <= 1}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addField}
                        size="small"
                        variant="outlined"
                    >
                        Add Field
                    </Button>
                </Box>
            </FormControl>
        </div>
    );
};

const RenderCraftTemplate = ({ node }) => {
    const { query } = useEditor();

    if (!node.data.nodes || node.data.nodes.length === 0) {
        return <div>No components in template</div>;
    }

    return (
        <div style={{ padding: '8px' }}>
            {node.data.nodes.map(childId => {
                const childNode = query.node(childId).get();
                return <Element key={childId} is={childNode.data.type} {...childNode.data.props} />;
            })}
        </div>
    );
};

export const CraftItemContainer = ({ children, ...props }) => {
    return <div {...props}>{children}</div>;
};

CraftItemContainer.craft = {
    rules: { canMoveIn: () => true }
};

VirtualCraftList.craft = {
    props: {
        name: "",
        width: "100%",
        height: "100%", // Changed default to 100%
        clickable: true,
        displayFields: [
            { key: '_id', label: 'ID' }
        ],
        showAllFields: false,
        onItemClick: null
    },
    related: {
        settings: VirtualCraftListSettings
    }
};